// src/components/DashboardMielinegraph.jsx

import React, { useState, useEffect } from 'react';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';
import supabase from '../../supabase';
import './DashboardMielinegraph.css';

const DashboardMielinegraph = ({ user, selectedPeriod }) => {
  const [chartData, setChartData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [hasError, setHasError] = useState(false);
  const [totalIncome, setTotalIncome] = useState(0);
  const [totalExpenses, setTotalExpenses] = useState(0);
  const [netBalance, setNetBalance] = useState(0);

  useEffect(() => {
    if (user?.id) {
      fetchMIELineData();
    }
    // eslint-disable-next-line
  }, [user?.id, selectedPeriod]);

  const fetchMIELineData = async () => {
    try {
      setLoading(true);
      setHasError(false);

      const [incomeData, expensesData] = await Promise.all([
        fetchIncomeData(),
        fetchExpensesData()
      ]);

      processLineChartData(incomeData, expensesData);

    } catch (error) {
      setHasError(true);
      resetChartData();
    } finally {
      setLoading(false);
    }
  };

  const fetchIncomeData = async () => {
    if (!user?.id) return [];
    const { data, error } = await supabase
      .from('income')
      .select('*')
      .eq('user_id', user.id)
      .order('income_date', { ascending: false });
    if (error) throw error;
    return data || [];
  };

  const fetchExpensesData = async () => {
    if (!user?.id) return [];
    const { data, error } = await supabase
      .from('expenses')
      .select('*')
      .eq('user_id', user.id)
      .order('expense_date', { ascending: false });
    if (error) throw error;
    return data || [];
  };

  const resetChartData = () => {
    setChartData([]);
    setTotalIncome(0);
    setTotalExpenses(0);
    setNetBalance(0);
  };

  const processLineChartData = (incomeRecords, expenseRecords) => {
    const safeIncomeRecords = Array.isArray(incomeRecords) ? incomeRecords : [];
    const safeExpenseRecords = Array.isArray(expenseRecords) ? expenseRecords : [];

    const { startDate, endDate, dateFormat } = getDateRange(selectedPeriod);

    const filteredIncome = safeIncomeRecords.filter(income => {
      if (!income.income_date) return false;
      const date = new Date(income.income_date);
      return !isNaN(date.getTime()) && date >= startDate && date <= endDate;
    });

    const filteredExpenses = safeExpenseRecords.filter(expense => {
      if (!expense.expense_date) return false;
      const date = new Date(expense.expense_date);
      return !isNaN(date.getTime()) && date >= startDate && date <= endDate;
    });

    let totalIncomeAmount = 0;
    let totalExpensesAmount = 0;

    filteredIncome.forEach(income => {
      const amount = parseFloat(income.total_amount || 0);
      if (!isNaN(amount)) totalIncomeAmount += amount;
    });

    filteredExpenses.forEach(expense => {
      // Try multiple possible amount fields
      const possibleAmounts = [
        expense.amount,
        expense.total_amount,
        expense.expense_amount,
        expense.value
      ];
      let amount = 0;
      for (const possibleAmount of possibleAmounts) {
        const parsed = parseFloat(possibleAmount || 0);
        if (!isNaN(parsed) && parsed > 0) {
          amount = parsed;
          break;
        }
      }
      if (amount > 0) totalExpensesAmount += amount;
    });

    const chartArray = generateChartData(filteredIncome, filteredExpenses, startDate, endDate, dateFormat);

    setChartData(chartArray);
    setTotalIncome(Math.round(totalIncomeAmount * 100) / 100);
    setTotalExpenses(Math.round(totalExpensesAmount * 100) / 100);
    setNetBalance(Math.round((totalIncomeAmount - totalExpensesAmount) * 100) / 100);
  };

  const getDateRange = (selectedPeriod) => {
    const now = new Date();
    let startDate, endDate, dateFormat;

    switch (selectedPeriod) {
      case 'this-month':
        startDate = new Date(now.getFullYear(), now.getMonth(), 1);
        endDate = new Date(now.getFullYear(), now.getMonth() + 1, 0);
        dateFormat = 'day';
        break;
      case 'last-month': {
        const lastMonth = new Date(now.getFullYear(), now.getMonth() - 1, 1);
        startDate = new Date(lastMonth.getFullYear(), lastMonth.getMonth(), 1);
        endDate = new Date(lastMonth.getFullYear(), lastMonth.getMonth() + 1, 0);
        dateFormat = 'day';
        break;
      }
      case 'last-3-months':
      case 'last-three-months':
        startDate = new Date(now.getFullYear(), now.getMonth() - 2, 1);
        endDate = new Date(now.getFullYear(), now.getMonth() + 1, 0);
        dateFormat = 'month';
        break;
      case 'last-6-months':
        startDate = new Date(now.getFullYear(), now.getMonth() - 5, 1);
        endDate = new Date(now.getFullYear(), now.getMonth() + 1, 0);
        dateFormat = 'month';
        break;
      case 'this-year':
        startDate = new Date(now.getFullYear(), 0, 1);
        endDate = new Date(now.getFullYear(), 11, 31);
        dateFormat = 'year-month';
        break;
      case 'last-year':
        startDate = new Date(now.getFullYear() - 1, 0, 1);
        endDate = new Date(now.getFullYear() - 1, 11, 31);
        dateFormat = 'year-month';
        break;
      default:
        startDate = new Date(now.getFullYear(), now.getMonth(), 1);
        endDate = new Date(now.getFullYear(), now.getMonth() + 1, 0);
        dateFormat = 'day';
    }
    endDate.setHours(23, 59, 59, 999);
    return { startDate, endDate, dateFormat };
  };

  const generateChartData = (incomeRecords, expenseRecords, startDate, endDate, dateFormat) => {
    const allPeriods = generatePeriods(startDate, endDate, dateFormat);
    const groupedData = {};
    allPeriods.forEach(period => {
      groupedData[period.key] = {
        period: period.label,
        income: 0,
        expenses: 0,
        net: 0,
        sortKey: period.sortKey
      };
    });

    incomeRecords.forEach(income => {
      if (!income.income_date) return;
      const date = new Date(income.income_date);
      if (isNaN(date.getTime())) return;
      const key = formatDateKey(date, dateFormat);
      if (groupedData[key]) {
        const amount = parseFloat(income.total_amount || 0);
        if (!isNaN(amount)) groupedData[key].income += amount;
      }
    });

    expenseRecords.forEach(expense => {
      if (!expense.expense_date) return;
      const date = new Date(expense.expense_date);
      if (isNaN(date.getTime())) return;
      const key = formatDateKey(date, dateFormat);
      if (groupedData[key]) {
        const possibleAmounts = [
          expense.amount,
          expense.total_amount,
          expense.expense_amount,
          expense.value
        ];
        let amount = 0;
        for (const possibleAmount of possibleAmounts) {
          const parsed = parseFloat(possibleAmount || 0);
          if (!isNaN(parsed) && parsed > 0) {
            amount = parsed;
            break;
          }
        }
        if (amount > 0) groupedData[key].expenses += amount;
      }
    });

    const chartArray = Object.values(groupedData)
      .sort((a, b) => a.sortKey - b.sortKey)
      .map(item => ({
        period: item.period,
        income: Math.round(item.income * 100) / 100,
        expenses: Math.round(item.expenses * 100) / 100,
        net: Math.round((item.income - item.expenses) * 100) / 100
      }));

    return chartArray;
  };

  const generatePeriods = (startDate, endDate, format) => {
    const periods = [];
    const current = new Date(startDate);

    if (format === 'day') {
      while (current <= endDate) {
        const key = formatDateKey(current, format);
        periods.push({
          key: key,
          label: current.getDate().toString(),
          sortKey: current.getTime()
        });
        current.setDate(current.getDate() + 1);
      }
    } else if (format === 'month' || format === 'year-month') {
      while (current <= endDate) {
        const key = formatDateKey(current, format);
        const monthNames = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
        periods.push({
          key: key,
          label: format === 'year-month' ? monthNames[current.getMonth()] : key,
          sortKey: current.getTime()
        });
        current.setMonth(current.getMonth() + 1);
      }
    }
    return periods;
  };

  const formatDateKey = (date, format) => {
    const year = date.getFullYear();
    const month = date.getMonth();
    const day = date.getDate();
    const monthNames = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

    switch (format) {
      case 'day':
        return `${year}-${String(month + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
      case 'month':
        return `${monthNames[month]} ${year}`;
      case 'year-month':
        return `${monthNames[month]} ${year}`;
      default:
        return `${monthNames[month]} ${year}`;
    }
  };

  const CustomTooltip = ({ active, payload, label }) => {
    if (active && payload && payload.length) {
      return (
        <div className="dashboardmielinegraph-tooltip">
          <p className="dashboardmielinegraph-tooltip-label">{label}</p>
          {payload.map((entry, index) => (
            <p key={index} className="dashboardmielinegraph-tooltip-value" style={{ color: entry.color }}>
              {entry.name}: ₹{entry.value.toLocaleString()}
            </p>
          ))}
        </div>
      );
    }
    return null;
  };

  const getXAxisProps = () => {
    if (selectedPeriod === 'this-month' || selectedPeriod === 'last-month') {
      return {
        angle: 0,
        textAnchor: 'middle',
        height: 60,
        interval: Math.max(0, Math.floor(chartData.length / 15))
      };
    }
    return {
      angle: -45,
      textAnchor: 'end',
      height: 80,
      interval: 0
    };
  };

  if (loading) {
    return (
      <div className="dashboardmielinegraph">
        <div className="dashboardmielinegraph-loading">
          <div className="dashboardmielinegraph-loading-spinner"></div>
          <p>Loading income vs expenses trend...</p>
        </div>
      </div>
    );
  }

  if (hasError) {
    return (
      <div className="dashboardmielinegraph">
        <div className="dashboardmielinegraph-error">
          <p>Failed to load income vs expenses trend. Please try again.</p>
          <button onClick={fetchMIELineData} className="retry-button">
            Retry
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="dashboardmielinegraph">
      <div className="dashboardmielinegraph-section">
        <div className="dashboardmielinegraph-section-header" />
        <div className="dashboardmielinegraph-chart">
          <div className="dashboardmielinegraph-chart-container">
            {chartData.length > 0 ? (
              <div className="dashboardmielinegraph-chart-wrapper">
                <ResponsiveContainer width="100%" height={450}>
                  <LineChart data={chartData} margin={{ top: 20, right: 30, left: 20, bottom: 60 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#e0e0e0" />
                    <XAxis
                      dataKey="period"
                      stroke="#666"
                      fontSize={12}
                      {...getXAxisProps()}
                    />
                    <YAxis
                      stroke="#666"
                      fontSize={12}
                      tickFormatter={(value) => `₹${(value / 1000).toFixed(0)}K`}
                    />
                    <Tooltip content={<CustomTooltip />} />
                    <Legend wrapperStyle={{ paddingTop: '20px' }} iconType="line" />
                    <Line
                      type="monotone"
                      dataKey="income"
                      stroke="#10B981"
                      strokeWidth={3}
                      dot={{ fill: '#10B981', strokeWidth: 2, r: 2 }}
                      activeDot={{ r: 7, stroke: '#10B981', strokeWidth: 2 }}
                      name="Income"
                    />
                    <Line
                      type="monotone"
                      dataKey="expenses"
                      stroke="#EF4444"
                      strokeWidth={3}
                      dot={{ fill: '#EF4444', strokeWidth: 2, r: 2 }}
                      activeDot={{ r: 7, stroke: '#EF4444', strokeWidth: 2 }}
                      name="Expenses"
                    />
                    <Line
                      type="monotone"
                      dataKey="net"
                      stroke="#6366F1"
                      strokeWidth={2}
                      strokeDasharray="5 5"
                      dot={{ fill: '#6366F1', strokeWidth: 2, r: 2 }}
                      activeDot={{ r: 6, stroke: '#6366F1', strokeWidth: 2 }}
                      name="Net Balance"
                    />
                  </LineChart>
                </ResponsiveContainer>
              </div>
            ) : (
              <div className="dashboardmielinegraph-no-data">
                <p>No data available for the selected period</p>
                <span>Add some income and expense records to see the trend</span>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default DashboardMielinegraph;
